import React from 'react';
import { product } from '../mock';
import { Star, ShieldCheck, Truck, RefreshCcw, Minus, Plus } from 'lucide-react';

export default function BuyBox({ onAddToCart }) {
  const [selected, setSelected] = React.useState('v2');
  const [qty, setQty] = React.useState(1);
  const variant = product.variants.find((v) => v.id === selected) || product.variants[0];

  const add = () => {
    onAddToCart({
      id: `${product.id}-${variant.id}`,
      name: `${product.name} — ${variant.label}`,
      subtitle: variant.qty,
      price: variant.price,
      image: product.image,
      qty,
    });
  };

  return (
    <section id="product" className="px-5 sm:px-8 mt-24">
      <div className="mx-auto max-w-[1400px]">
        <div className="grid lg:grid-cols-2 gap-10 items-center rounded-3xl overflow-hidden bg-white dark:bg-neutral-950 border border-black/5 dark:border-white/5">
          <div className="relative bg-[color:var(--cv-teal-soft)] dark:bg-neutral-900 h-full min-h-[520px] flex items-center justify-center p-10">
            <div className="absolute inset-8 rounded-3xl border border-white/50 dark:border-white/5" />
            <div className="relative z-10 w-[380px] h-[440px] product-crop-container">
              <img src={product.image} alt={product.name} className="w-full h-full object-cover product-float product-crop drop-shadow-[0_35px_45px_rgba(17,138,148,0.25)]" />
            </div>
            <div className="absolute top-6 left-6 inline-flex items-center gap-2 bg-white/95 dark:bg-neutral-900/95 backdrop-blur px-3 py-1.5 rounded-full text-[10px] font-semibold tracking-[0.22em] uppercase shadow-sm">
              <span className="tag-dot" /> Best Seller
            </div>
          </div>

          <div className="p-6 sm:p-10">
            <div className="flex items-center gap-3">
              <div className="flex items-center gap-1 text-teal">
                {Array.from({ length: 5 }).map((_, i) => <Star key={i} size={14} fill="currentColor" />)}
              </div>
              <span className="text-[13px] text-neutral-500">{product.rating} · {product.reviews} reviews</span>
            </div>
            <h2 className="font-grotesk mt-3 text-4xl sm:text-5xl tracking-[-0.02em] font-medium">{product.name}</h2>
            <p className="mt-2 text-neutral-500">{product.subtitle}</p>
            <p className="mt-4 text-[15px] text-neutral-600 dark:text-neutral-300 leading-relaxed">{product.short}</p>

            <div className="mt-6 space-y-2">
              {product.variants.map((v) => {
                const active = v.id === selected;
                return (
                  <button key={v.id} onClick={() => setSelected(v.id)} className={`w-full text-left rounded-2xl border p-4 flex items-center justify-between transition ${active ? 'border-[color:var(--cv-teal)] bg-[color:var(--cv-teal-soft)] dark:bg-white/5' : 'border-black/10 dark:border-white/10 hover:border-[color:var(--cv-teal)]'}`}>
                    <div className="flex items-center gap-3">
                      <span className={`h-4 w-4 rounded-full border-2 ${active ? 'border-[color:var(--cv-teal)] bg-[color:var(--cv-teal)]' : 'border-neutral-300'}`} />
                      <div>
                        <div className="flex items-center gap-2">
                          <div className="font-grotesk font-semibold">{v.label}</div>
                          {v.badge && <span className="text-[10px] font-semibold uppercase tracking-widest text-teal bg-white dark:bg-neutral-950 border border-[color:var(--cv-teal)] px-2 py-0.5 rounded-full">{v.badge}</span>}
                        </div>
                        <div className="text-[12px] text-neutral-500">{v.qty}</div>
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="font-grotesk font-semibold">₹{v.price.toLocaleString()}</div>
                      <div className="text-[12px] text-neutral-400 line-through">₹{v.compareAt.toLocaleString()}</div>
                    </div>
                  </button>
                );
              })}
            </div>

            <div className="mt-6 flex items-center gap-3">
              <div className="inline-flex items-center rounded-full border border-black/10 dark:border-white/10">
                <button onClick={() => setQty((q) => Math.max(1, q-1))} className="h-11 w-11 inline-flex items-center justify-center"><Minus size={14} /></button>
                <span className="w-8 text-center font-semibold">{qty}</span>
                <button onClick={() => setQty((q) => q+1)} className="h-11 w-11 inline-flex items-center justify-center"><Plus size={14} /></button>
              </div>
              <button onClick={add} className="flex-1 h-12 rounded-full bg-[color:var(--cv-teal)] text-white font-semibold hover:bg-[color:var(--cv-teal-dark)] transition">
                Add to Cart — ₹{(variant.price * qty).toLocaleString()}
              </button>
            </div>

            <div className="mt-6 grid grid-cols-3 gap-3 text-[12px]">
              <div className="flex items-center gap-2"><ShieldCheck size={16} className="text-teal shrink-0" /> Lab-tested purity</div>
              <div className="flex items-center gap-2"><Truck size={16} className="text-teal shrink-0" /> Free shipping over ₹999</div>
              <div className="flex items-center gap-2"><RefreshCcw size={16} className="text-teal shrink-0" /> 30-day guarantee</div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
